import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FavoriteIcon from '@mui/icons-material/Favorite';
import DiamondIcon from '@mui/icons-material/Diamond';
import CakeIcon from '@mui/icons-material/Cake';
import { getPhotosByMonth } from '../services/api';
import { formatDateToYYYYMMDD, getRatingColor, isPhotoDraft, DRAFT_COLOR } from '../utils/dateUtils';
import { getSpecialDate } from '../data/specialDates';

const CalendarPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => {
    fetchPhotos();
  }, [year, month]);

  const fetchPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPhotosByMonth(year, month);
      setPhotos(data);
    } catch (err) {
      setError('Failed to load photos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    // Returns 0-6 where 0 = Monday, 6 = Sunday
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert Sunday (0) to 6, and shift others down by 1
    return day === 0 ? 6 : day - 1;
  };

  const hasPhoto = (day) => {
    const date = new Date(year, month - 1, day);
    const dateString = formatDateToYYYYMMDD(date);
    // Date is already in YYYY-MM-DD string format
    return photos.some((photo) => photo.date === dateString);
  };

  const getPhotoForDate = (day) => {
    const date = new Date(year, month - 1, day);
    const dateString = formatDateToYYYYMMDD(date);
    // Date is already in YYYY-MM-DD string format
    return photos.find((photo) => photo.date === dateString);
  };

  const getSpecialDateForDay = (day) => {
    return getSpecialDate(month, day);
  };

  const getSpecialDateIcon = (specialDate) => {
    if (!specialDate) return null;
    
    const iconProps = {
      sx: {
        fontSize: { xs: 14, sm: 16, md: 18 },
        color: specialDate.type === 'anniversary' ? '#e91e63' : 
               specialDate.type === 'propose' ? '#9c27b0' : 
               '#ff9800',
      },
    };

    switch (specialDate.icon) {
      case 'favorite':
        return <FavoriteIcon {...iconProps} />;
      case 'diamond':
        return <DiamondIcon {...iconProps} />;
      case 'cake':
        return <CakeIcon {...iconProps} />;
      default:
        return null;
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(year, month - 2, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month, 1));
  };

  const handleDateClick = (day) => {
    const date = new Date(year, month - 1, day);
    const dateString = formatDateToYYYYMMDD(date);
    const photo = getPhotoForDate(day);
    if (photo && !isPhotoDraft(photo)) {
      navigate(`/date/${photo.date}`);
    } else {
      navigate(`/upload?date=${dateString}`);
    }
  };

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }

  // Add cells for each day of the month
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  // Add empty cells to complete the last row (always 7 columns)
  while (days.length % 7 !== 0) {
    days.push(null);
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        pt: { xs: 10, sm: 12 }, 
        mb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 2, sm: 3 }, 
          maxWidth: 800, 
          mx: 'auto',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Button
          onClick={handlePreviousMonth}
          startIcon={<ArrowBackIosIcon />}
          sx={{ mr: { xs: 0, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          size={isSmallScreen ? 'small' : 'medium'}
        >
          Previous
        </Button>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            flexGrow: 1, 
            textAlign: 'center',
            fontSize: { xs: '1.5rem', sm: '2rem' },
            order: { xs: -1, sm: 0 },
            width: { xs: '100%', sm: 'auto' },
            mb: { xs: 2, sm: 3 },
          }}
        >
          {monthNames[month - 1]} {year}
        </Typography>
        <Button
          onClick={handleNextMonth}
          endIcon={<ArrowForwardIosIcon />}
          sx={{ ml: { xs: 0, sm: 2 }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          size={isSmallScreen ? 'small' : 'medium'}
        >
          Next
        </Button>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 1, sm: 2 } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: { xs: 0.5, sm: 1 },
            maxWidth: { xs: '100%', sm: 700 },
            mx: 'auto',
          }}
        >
          {/* Calendar days */}
          {days.map((day, index) => {
            const photo = day ? getPhotoForDate(day) : null;
            const isDraft = photo ? isPhotoDraft(photo) : false;
            const ratingColor = photo && !isDraft ? getRatingColor(photo.rating) : null;
            const dayColor = isDraft ? DRAFT_COLOR : ratingColor;
            const specialDate = day ? getSpecialDateForDay(day) : null;
            const isSpecial = specialDate !== null;
            
            return (
              <Box
                key={index}
                sx={{
                  aspectRatio: '1',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  cursor: day ? 'pointer' : 'default',
                  bgcolor: photo
                    ? `${dayColor}22`
                    : day
                    ? 'background.paper'
                    : '#fafafa',
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': photo
                    ? { bgcolor: dayColor, color: 'white' }
                    : day ? { borderColor: '#bdbdbd', transform: 'scale(1.05)' } : {},
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => day && handleDateClick(day)}
                title={isSpecial ? specialDate.label : undefined}
              >
                {day && (
                  <>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                        fontWeight: isSpecial ? 600 : 400,
                      }}
                    >
                      {day}
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      gap: 0.5,
                      mt: 0.5,
                    }}>
                      {isSpecial && getSpecialDateIcon(specialDate)}
                      {photo && (
                        <Box
                          sx={{
                            width: { xs: 6, sm: 8, md: 10 },
                            height: { xs: 6, sm: 8, md: 10 },
                            borderRadius: '50%',
                            bgcolor: dayColor,
                          }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Container>
  );
};

export default CalendarPage;

